import React, { ChangeEventHandler, useEffect, useMemo, useRef, useState, PropsWithChildren, useCallback } from "react"
import { graphql, useStaticQuery } from "gatsby"
import { Geocoder } from "@dotdev/reactive-google-map"
import { useCore, useStorage } from "@hooks/useCore"
import { useCart } from "@hooks/useCart"
import { useFunctions } from "@hooks/useFunctions"
import { useCartContext } from "@providers/cart"
import { useConfigContext } from "@providers/config"
import config from "../../config.js"
import { useAppContext } from "@providers/app"

// type SearchQuery = {
//   items: string[]
//   search?: string
// }

type BadgeProps = {
  variant: "success" | "error" | "warning"
  children: string
}

const geocoder = new Geocoder({
  googleMapsApiKey: config.store.googleMapsApiKey,
  googleMapsApiVersion: config.store.googleMapsApiVersion,
})

const defaultPosition: LatLng = { lat: -25.274398, lng: 133.775136 }

type ContextProps = {
  locations: NormalisedStore[]
  loadingLocations: boolean
  loadingCheckout: boolean
  filterInStock: boolean
  selected: number | null
  activeStore: object | null
  search: string
  productCount: number
  getBadgeProps: (store: NormalisedStore, showSingleStockValue: boolean) => BadgeProps
  updateSearch: ChangeEventHandler<HTMLInputElement>
  toggleFilterInStock: VoidFunction
  setStore: (location: NormalisedStore | null) => void
  useMyLocation: VoidFunction
}

export interface ClickAndCollectProviderProps extends PropsWithChildren {
  productSkus: string[]
  productCount: number
  noActiveStore?: boolean
  isCartStore?: boolean
  showClickAndCollect?: boolean
  children: any
  drawerRef?: any
}

export const ClickAndCollectContext = React.createContext<ContextProps | undefined>(undefined)

export const ClickAndCollectProvider = ({
  children,
  productSkus,
  noActiveStore,
  isCartStore,
  productCount,
  drawerRef,
  showClickAndCollect = false,
}: ClickAndCollectProviderProps) => {
  const { cart } = useCartContext()
  const checkoutRef = useRef(cart)
  const { dispatch } = useAppContext()
  const [loadingLocations, setLoadingLocations] = useState<boolean>(false)
  const [loadingCheckout, setLoadingCheckout] = useState<boolean>(false)
  const [filterInStock, setFilterInStock] = useState<boolean>(false)
  const [locations, setLocations] = useState<NormalisedStore[]>([])
  const [position, setPosition] = useState<LatLng | null>(defaultPosition)
  const [search, setSearch] = useState<string>("")
  const [selected, setSelected] = useState<number | null>(null)
  const [activeStore, setActiveStore] = useState<string | null>(null)
  const [storeTrigger, setStoreTrigger] = useState<boolean>(false)
  const activeStoreRef = useRef()
  const previousProductSkusRef = useRef(productSkus)
  const { updateCartAttributes } = useCart()
  const { callFunction } = useFunctions()
  const {
    helpers: { edgeNormaliser },
  } = useCore()
  const { getStorage, setStorage } = useStorage()
  const {
    settings: { keys },
  } = useConfigContext()

  const { stores: sanityStores } = useStaticQuery<GatsbyTypes.FindInStoreQuery>(graphql`
    query FindInStore {
      stores: allSanityStore(filter: { type: { eq: "Store" }, ap21StoreNumber: { ne: null } }) {
        edges {
          node {
            title
            phone
            email
            address
            location {
              lat
              lng
              alt
            }
            openhours {
              day
              hours
            }
            ap21StoreNumber
            hiddenCC
            description
            handle {
              current
            }
            website
          }
        }
      }
    }
  `)

  const compareActiveStores = useCallback((as1, as2) => {
    if (as1?.id !== as2?.id || as1?.ap21StoreNumber !== as2?.ap21StoreNumber) {
      return false
    }

    if (as1?.locationCartQuantities !== as2?.locationCartQuantities) {
      return false
    }

    const as1Skus = Object.keys(as1?.locationCartQuantities || {})
    const as2Skus = Object.keys(as2?.locationCartQuantities || {})
    const sameItems =
      as1Skus?.every(itemKey => {
        return as2?.locationCartQuantities?.[itemKey] === as1?.locationCartQuantities?.[itemKey]
      }) &&
      as2Skus?.every(itemKey => {
        return as2?.locationCartQuantities?.[itemKey] === as1?.locationCartQuantities?.[itemKey]
      })

    if (!sameItems) {
      return false
    }

    const sameStockStatus = as1?.isAllInStock === as2?.isAllInStock

    if (!sameStockStatus) {
      return false
    }

    if (as1 === as2) {
      return true
    }

    return true
  }, [])

  React.useEffect(() => {
    const sameStores = compareActiveStores(activeStoreRef.current, activeStore)
    if (isCartStore && !sameStores) {
      activeStoreRef.current = activeStore
      dispatch({ type: "store", payload: { activeCartStore: activeStore } })
    }
  }, [activeStore, isCartStore, dispatch, compareActiveStores, productSkus])

  React.useEffect(() => {
    checkoutRef.current = cart
  }, [cart])

  const normalisedSanityStores = useMemo(() => {
    return edgeNormaliser(sanityStores)
  }, [edgeNormaliser, sanityStores])

  const getBadgeLabel = useCallback(
    (store: NormalisedStore, showSingleStockValue: boolean): string => {
      if (productCount === 1 || showSingleStockValue) {
        return store.stockAvailable > 0 ? `Available for pickup` : "Unavailable for pickup"
      }
      const stockAvailable = store?.totalCartStock

      const outOfToShow = stockAvailable >= productCount ? productCount : stockAvailable

      return stockAvailable > 0 ? `${outOfToShow}/${productCount} available for pickup` : "All unavailable for pickup"
    },
    [productCount]
  )

  const getBadgeIcon = useCallback((variant: string) => {
    if (variant === "error") return "close"
    if (variant === "success") return "simpleTick"
    if (variant === "warning") return "error"
  }, [])

  const getBadgeProps = useCallback(
    (store: NormalisedStore, showSingleStockValue: boolean): BadgeProps => {
      if (!store) {
        store = {
          stockAvailable: 0,
          items: [{}],
        }
      }
      const label = getBadgeLabel(store, showSingleStockValue)
      let stockAvailable = store?.totalCartStock
      if (productCount === 1) {
        if (store?.stockAvailable > 0) {
          stockAvailable = 1
        } else {
          stockAvailable = 0
        }
      }
      if (stockAvailable === 0) {
        return { variant: "error", icon: getBadgeIcon("error"), label, ccAvailable: false }
      } else if (stockAvailable >= productCount || showSingleStockValue) {
        return { variant: "success", icon: getBadgeIcon("success"), label, ccAvailable: true }
      } else {
        return { variant: "warning", icon: getBadgeIcon("warning"), label, ccAvailable: false }
      }
    },
    [getBadgeIcon, getBadgeLabel, productCount]
  )

  const deg2rad = useCallback((deg: number) => {
    return deg * (Math.PI / 180)
  }, [])

  const getDistance = useCallback(
    (pos1: any, pos2: any) => {
      if (!pos1 || !pos2) {
        return 0
      }

      const R = 6371 // Radius of the earth in km
      const dLat = deg2rad(pos2.lat - pos1.lat) // deg2rad below
      const dLon = deg2rad(pos2.lng - pos1.lng)
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(deg2rad(pos1.lat)) * Math.cos(deg2rad(pos2.lat)) * Math.sin(dLon / 2) * Math.sin(dLon / 2)
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
      return R * c
    },
    [deg2rad]
  )

  const focusDrawer = useCallback(() => {
    if (drawerRef?.current) {
      drawerRef.current?.focus()
      // window?.focus()
    }
  }, [drawerRef])

  const setStore = useCallback(
    async (location: NormalisedStore | null) => {
      const customAttributes: CustomAttribute[] = [
        { key: "_click_and_collect", value: location ? "true" : "false" },
        { key: "pickup_store_id", value: location?.ap21StoreNumber || location?.id?.toString?.() || "" },
        { key: "pickup_store_number", value: location?.id?.toString?.() || "" },
        { key: "pickup_store_name", value: location?.name?.toString() || "" },
      ]
      setStorage(keys?.store, JSON.stringify(location))
      setActiveStore(location)

      setLoadingCheckout(true)
      await updateCartAttributes(customAttributes)
      setLoadingCheckout(false)
      focusDrawer()
    },
    [keys?.store, setStorage, updateCartAttributes, focusDrawer]
  )

  const updateSearch = useCallback(async pos => {
    setPosition(pos), [setPosition]
    setStoreTrigger(true);
  })

  const useMyLocation = useCallback(async () => {
    const position = await geocoder.browserToLatLng()
    const address = await geocoder.latLngToAddress(position)

    setPosition(position)
    setSearch(address.formatted_address || "")
  }, [setPosition, setSearch])

  const pickupStore = useMemo(() => {
    return cart?.attributes?.find(({ key }: any) => key === "pickup_store_number")?.value
  }, [cart])

  const isCCAvailable = useMemo(() => {
    return getBadgeProps(activeStore).ccAvailable
  }, [activeStore, getBadgeProps])

  const shallowCompare = useCallback((a: Array<string>, b: Array<string>) => {
    if (a === b) {
      return true
    }

    if (a.length !== b.length) {
      return false
    }

    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) {
        return false
      }
    }

    return true
  }, [])

  const toggleFilterInStock = useCallback(() => setFilterInStock(filterInStock => !filterInStock), [setFilterInStock])

  const normaliseLocation = useCallback(
    ({ attributes }: any): NormalisedStore => {
      const sanityStore = normalisedSanityStores?.find(({ ap21StoreNumber }) => ap21StoreNumber == attributes?.storeId)

      return {
        id: attributes?.storeNumber,
        name: attributes?.storeName,
        store_id: attributes?.storeId,
        ...(sanityStore && { ...sanityStore }),
        ...(sanityStore?.location && { distance: getDistance(sanityStore?.location, position) }),
      }
    },
    [getDistance, position, normalisedSanityStores]
  )

  const [checkoutLineItems, setCheckoutLineItems] = useState<LineItem[]>([])

  useEffect(() => {
    if (!checkoutLineItems?.length && cart?.lines?.length) {
      setCheckoutLineItems(cart?.lines)
    } else {
      const allLineItemsSame = cart?.lines?.every(item => {
        return checkoutLineItems?.find(item2 => item2?.merchandise?.id === item?.merchandise?.id && item2?.quantity === item?.quantity)
      })
      if (!allLineItemsSame) {
        setCheckoutLineItems(cart?.lines)
      }
    }
  }, [cart, checkoutLineItems, shallowCompare])

  const latestRequest = useRef(0)

  const fetchLocations = useCallback(
    async productSkus => {
      try {
        setLoadingLocations(true)
        const requestTime = Date.now()
        latestRequest.current = requestTime
        const { status, body: locations } = await callFunction("check-stock-ap21", { skus: productSkus })
        if (requestTime >= latestRequest.current) {
          if (status !== "success") {
            console.error("Error fetching locations", status, locations)
            setLoadingLocations(false)
            return
          }
          const ap21Data = locations
            .map((item: any) => item?.data)
            .flat()
            .filter(item => item?.attributes?.storeName !== "Online")
            .reduce((arr: any[], item: any) => {
              const index = arr.findIndex(store => store.storeNumber === item.attributes.storeNumber)
              if (index === -1) {
                arr.push({
                  storeNumber: item.attributes.storeNumber,
                  items: {
                    [`${item.attributes.sku}`]: item.attributes?.freeStock,
                  },
                  stockAvailable: item.attributes?.freeStock ? 1 : 0,
                  ...normaliseLocation(item),
                })
              } else {
                arr[index].items[`${item.attributes.sku}`] = item.attributes?.freeStock
              }

              return arr
            }, [])

          const items = productSkus.reduce((acc, sku) => {
            acc[sku] = 0
            return acc
          }, {})

          const sanityStoreData = normalisedSanityStores
            ?.map(store => {
              return {
                ...store,
                items: items,
                store_id: +store.ap21StoreNumber,
                stockAvailable: 0,
                name: store.title,
                isAllInStock: null,
                ...(store?.location && { distance: getDistance(store?.location, position) }),
              }
            })
            ?.filter(store => !ap21Data?.find(({ store_id }) => store_id === store.store_id))

          const locationsToSet = [...ap21Data, ...sanityStoreData].sort((a, b) => a.distance - b.distance)
          locationsToSet.forEach(location => {
            const itemsMissing = productSkus.filter(sku => !location?.items[sku])
            itemsMissing.forEach(sku => {
              location.items[sku] = 0
            })
            location.cartLineItems = checkoutLineItems
            // this ensures that the special gift wrap item is always available for pickup
            Object.keys(location?.items || {})?.forEach(itemKey => {
              if (itemKey.includes("PBB10-ONLINE")) {
                location.items[itemKey] = 999
              }
            })
            location.locationCartQuantities = {}
            location.isAllInStock = null
            Object.keys(location?.items)?.forEach((itemKey: any) => {
              const locationItemQuantity = location?.items[itemKey]
              const lineItem = checkoutLineItems?.find(({ merchandise: { sku } }) => sku === itemKey)
              // this creates an integer representing the total fulfillable stock capped at the line item quantity
              location.locationCartQuantities[itemKey] =
                lineItem?.quantity <= locationItemQuantity ? lineItem?.quantity : locationItemQuantity
              // if any items have signaled out of stock, keep the location out of stock
              if (location.isAllInStock === false) {
                return
              } else {
                // if the cart hasn't been loaded yet we consider all products in stock for the location
                // if the location has stock for this product
                if (!lineItem?.quantity) {
                  location.isAllInStock = locationItemQuantity ? true : false
                } else {
                  location.isAllInStock = lineItem?.quantity <= locationItemQuantity
                }
              }
            })
            location.totalCartStock = Object.keys(location?.locationCartQuantities)?.reduce((acc, itemKey) => {
              acc += location?.locationCartQuantities[itemKey]
              return acc
            }, 0)
          })
          setLocations(locationsToSet)
          setLoadingLocations(false)
          setStoreTrigger(false);
        }
      } catch (err) {
        console.error(err)
        setLoadingLocations(false)
      }
      return
    },
    [callFunction, normaliseLocation, checkoutLineItems, normalisedSanityStores, getDistance, position]
  )

  const filteredLocations = useMemo(() => {
    if (!locations) return []
    const parsedLocations = locations?.map(location => {
      const items = location?.items
      return {
        ...location,
        stockCount: items?.length,
      }
    })

    if (filterInStock) {
      const filtered = parsedLocations.filter(location => location?.isAllInStock)
      return filtered
    }
    return parsedLocations
  }, [filterInStock, locations])

  useEffect(() => {
    setSelected(+pickupStore || null)
  }, [pickupStore])

  const pickupStoreRef = useRef(pickupStore)

  useEffect(() => {
    focusDrawer()
  }, [locations, focusDrawer, drawerRef])

  useEffect(() => {
    if (!pickupStoreRef?.current || pickupStoreRef?.current !== pickupStore) {
      pickupStoreRef.current = pickupStore
      const activeStoreString = getStorage(keys?.store)
      if (activeStoreString) {
        const activeStore = JSON.parse(activeStoreString)
        if (activeStore?.ap21StoreNumber !== pickupStore) {
          setActiveStore(activeStore)
        }
      }
    }
  }, [pickupStore, fetchLocations, activeStore, productSkus, getStorage, keys?.store])

  const initializeActiveStore = useCallback(
    (force?: boolean) => {
      if (!noActiveStore) {
        const storageStore = getStorage(keys?.store)
        if (storageStore) {
          const parsedStorageStore = JSON.parse(storageStore)
          const isInStockStore = filteredLocations?.find(({ ap21StoreNumber }) => ap21StoreNumber === parsedStorageStore?.ap21StoreNumber)
          let newActiveStore = parsedStorageStore
          if (isInStockStore) {
            newActiveStore = isInStockStore
          }
          const newActiveStoreSkus = Object.keys(newActiveStore?.locationCartQuantities || {})
          const parsedStorageStoreSkus = Object.keys(parsedStorageStore?.locationCartQuantities || {})
          const sameItems =
            newActiveStoreSkus?.every(itemKey => {
              return parsedStorageStore?.locationCartQuantities?.[itemKey] === newActiveStore?.locationCartQuantities?.[itemKey]
            }) &&
            parsedStorageStoreSkus?.every(itemKey => {
              return parsedStorageStore?.locationCartQuantities?.[itemKey] === newActiveStore?.locationCartQuantities?.[itemKey]
            })
          const sameStockStatus = parsedStorageStore?.isAllInStock === newActiveStore?.isAllInStock
          if (parsedStorageStore?.ap21StoreNumber !== newActiveStore?.ap21StoreNumber || !sameItems || !sameStockStatus) {
            setStorage(keys?.store, JSON.stringify(newActiveStore))
            setActiveStore(newActiveStore)
          }
        }
        if (force) {
          const forcedStore = getStorage(keys?.store)
          if (forcedStore) {
            const parsedForcedStore = JSON.parse(forcedStore)
            if (activeStore !== parsedForcedStore) {
              setActiveStore(parsedForcedStore)
            }
          }
        }
      }
    },
    [filteredLocations, noActiveStore, keys?.store, getStorage, setStorage, activeStore]
  )

  useEffect(() => {
    initializeActiveStore(true)
  }, [initializeActiveStore])

  useEffect(() => {
    initializeActiveStore()
  }, [initializeActiveStore])

  useEffect(() => {
    ;(async () => {
      if ((showClickAndCollect || activeStoreRef?.current) && productSkus?.length) {
        if (locations?.length === 0 || shallowCompare(productSkus, previousProductSkusRef.current) == false || storeTrigger) {
          await fetchLocations(productSkus)
          previousProductSkusRef.current = productSkus
        }
      }
    })()
  }, [showClickAndCollect, activeStoreRef, fetchLocations, locations, shallowCompare, previousProductSkusRef, productSkus, position, storeTrigger])

  const contextValue = React.useMemo<ContextProps>(
    () => ({
      activeStore,
      setActiveStore,
      loadingLocations,
      loadingCheckout,
      filterInStock,
      isCCAvailable,
      locations: filteredLocations,
      selected,
      fetchLocations,
      search,
      productCount,
      productSkus,
      getBadgeProps,
      updateSearch,
      toggleFilterInStock,
      setStore,
      useMyLocation,
    }),
    [
      activeStore,
      setActiveStore,
      loadingLocations,
      loadingCheckout,
      filterInStock,
      isCCAvailable,
      filteredLocations,
      fetchLocations,
      selected,
      search,
      productSkus,
      productCount,
      getBadgeProps,
      updateSearch,
      toggleFilterInStock,
      setStore,
      useMyLocation,
    ]
  )

  return <ClickAndCollectContext.Provider value={contextValue}>{children}</ClickAndCollectContext.Provider>
}

export const useClickAndCollectContext = (): ContextProps => ({ ...React.useContext(ClickAndCollectContext) } as ContextProps)
